import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core'
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms'

/** YB-2150 bounds of a stay limit, in minutes — PAPI refuses anything else with `max_stay_minutes_out_of_range`. */
export const MIN_STAY_MINUTES = 5
export const MAX_STAY_MINUTES = 24 * 60

/** Empty (no limit / follow the role), or a whole number of minutes from 5 to 1440 — the same rule as PAPI's isValidMaxStay. */
export function isValidStayMinutes(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true
  return typeof value === 'number' && Number.isInteger(value) && value >= MIN_STAY_MINUTES && value <= MAX_STAY_MINUTES
}

/** Minutes as the box shows them, "HH:MM" ('' when there is no value): 150 → "02:30", 1440 → "24:00". */
export function formatStayMinutes(total: number | null | undefined): string {
  if (total === null || total === undefined || !Number.isFinite(+total)) return ''
  const value = Math.trunc(+total)
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`
}

/**
 * What the user typed, as the mask keeps it: up to 4 digits, the first two the hours. A colon
 * typed after a single digit pads the hours, so "2:30" reads as 02:30.
 */
export function stayDigits(raw: string | null | undefined): string {
  const text = String(raw ?? '')
  const colon = text.indexOf(':')
  let digits = text.replace(/\D/g, '')
  if (colon >= 0 && text.slice(0, colon).replace(/\D/g, '').length === 1) digits = `0${digits}`
  return digits.slice(0, 4)
}

/** The digits as the box shows them while typing: "0", "02", "02:3", "02:30" (a typed colon stays: "02:"). */
export function maskStayDigits(digits: string, typedColon = false): string {
  if (digits.length > 2) return `${digits.slice(0, 2)}:${digits.slice(2)}`
  return digits.length === 2 && typedColon ? `${digits}:` : digits
}

/**
 * The box back to minutes. Empty — or 00:00 — is null (no value: follow the role / no limit), so
 * clearing the box, or a leftover 0, never blocks a save. One or two digits are whole hours ("2" =
 * 120); a third digit is the tens of the minutes ("023" = 02:30). Minutes past 59 give NaN, which
 * the validator refuses.
 */
export function parseStayDigits(digits: string): number | null {
  if (!digits) return null
  const hours = Number(digits.slice(0, 2))
  const minutes = digits.length <= 2 ? 0 : Number(digits.slice(2).padEnd(2, '0'))
  if (minutes > 59) return NaN
  const total = hours * 60 + minutes
  return total === 0 ? null : total
}

/**
 * The stay limit (זמן שהייה) of a client role or a pass — YB-2153.
 *
 * One form value in MINUTES (or null) behind a single "HH:MM" box, so the role drawers and the
 * pass modals hold the same number PAPI stores and never convert it themselves. It validates
 * itself (00:05 – 24:00), so a host only binds `formControlName`. The box can be typed into, or a
 * click opens a panel: hours / minutes steppers, quick picks and a clear button; ↑↓ step 5 min
 * (Shift: an hour).
 *
 * On a pass, `roleMinutes` is the role's default: it is shown as the placeholder and named in the
 * hint, and it is NEVER written into the value — an empty pass value means "follow the role", so
 * a later change to the role still reaches the pass.
 */
@Component({
  selector: 'app-stay-duration-input',
  templateUrl: './stay-duration-input.component.html',
  styleUrls: ['./stay-duration-input.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => StayDurationInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => StayDurationInputComponent), multi: true },
  ],
})
export class StayDurationInputComponent implements ControlValueAccessor, Validator {
  /** Id of the box; the error is `<id>-error`, the hint `<id>-hint` */
  @Input() inputId = 'max-stay-minutes'

  /** 'role' = the default itself (empty = no limit); 'pass' = an override of the role's default */
  @Input() mode: 'role' | 'pass' = 'role'

  /** Pass mode only: the selected role's default, null when the role has no limit */
  @Input() roleMinutes: number | null | undefined = null

  /** false = read-only (a details drawer) */
  @Input() editable = true

  /** The box's text: "HH:MM", or part of it while typing */
  text = ''

  disabled = false

  private onChange: (value: number | null) => void = () => undefined

  private onTouched: () => void = () => undefined

  constructor(private cdr: ChangeDetectorRef) {}

  get value(): number | null {
    return parseStayDigits(stayDigits(this.text))
  }

  get invalid(): boolean {
    return !isValidStayMinutes(this.value)
  }

  /** The role's default as "HH:MM" on a pass ('' when the role has none, or on the role itself) */
  get inherited(): string {
    return this.mode === 'pass' ? formatStayMinutes(this.roleMinutes) : ''
  }

  writeValue(value: number | null): void {
    this.text = formatStayMinutes(value)
    this.cdr.markForCheck()
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
    this.cdr.markForCheck()
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return isValidStayMinutes(control.value) ? null : { stayRange: { min: MIN_STAY_MINUTES, max: MAX_STAY_MINUTES } }
  }

  /** The box changed: keep it masked as HH:MM and emit the minutes */
  onInput(raw: string, box?: HTMLInputElement): void {
    this.text = maskStayDigits(stayDigits(raw), /:$/.test(String(raw ?? '')))
    if (box && box.value !== this.text) box.value = this.text
    this.onChange(this.value)
  }

  /** Leaving the box completes what was typed ("2" → 02:00, "02:3" → 02:30); 00:00 empties it */
  onBlur(): void {
    const value = this.value
    if (value === null) this.text = ''
    else if (Number.isFinite(value)) this.text = formatStayMinutes(value)
    this.onTouched()
    this.cdr.markForCheck()
  }

  // ── the picker panel (QA round of 2026-11-03: typing HH:MM alone was hard to edit) ────────────

  /** Quick picks, in minutes */
  readonly presets = STAY_PRESETS

  get canEdit(): boolean {
    return this.editable && !this.disabled
  }

  /** What the steppers start from: the value, else (on a pass) the role's, else nothing */
  private get base(): number | null {
    const own = this.value
    if (own !== null && Number.isFinite(own)) return own
    return this.mode === 'pass' && this.roleMinutes ? Number(this.roleMinutes) : null
  }

  /** The steppers' two numbers, "--" while there is nothing to show */
  get parts(): { hours: string; minutes: string } {
    const text = formatStayMinutes(this.base)
    return text ? { hours: text.slice(0, 2), minutes: text.slice(3) } : { hours: '--', minutes: '--' }
  }

  /** Set the value from the panel (null clears it: no limit / follow the role) */
  set(minutes: number | null): void {
    this.text = formatStayMinutes(minutes)
    this.onChange(this.value)
    this.onTouched()
    this.cdr.markForCheck()
  }

  /** ▲▼ (and the arrow keys): ±5 min on the 5-minute grid, ±60 for hours, kept within 00:05 – 24:00 */
  step(delta: number): void {
    if (!this.canEdit) return
    this.set(stepStayMinutes(this.base, delta))
  }

  onKey(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      this.step((event.key === 'ArrowUp' ? 1 : -1) * (event.shiftKey ? 60 : 5))
    }
  }

  presetLabel(minutes: number): { key: string; params: Record<string, number> } {
    if (minutes < 60) return { key: 'SETTINGS.LABELS.STAY.PRESET_MINUTES', params: { minutes } }
    if (minutes === 60) return { key: 'SETTINGS.LABELS.STAY.PRESET_HOUR', params: {} }
    if (minutes === 120) return { key: 'SETTINGS.LABELS.STAY.PRESET_TWO_HOURS', params: {} }
    return { key: 'SETTINGS.LABELS.STAY.PRESET_HOURS', params: { hours: minutes / 60 } }
  }
}

/** The panel's quick picks, in minutes */
export const STAY_PRESETS = [15, 30, 60, 120, 240, 480]

/**
 * One ▲▼ press. Minutes move on the 5-minute grid (07 ▲ → 10), hours by 60. Nothing to start from:
 * ▲ gives the first step (00:05 or 01:00), ▼ gives nothing. Never below 00:05 (clearing is its own
 * button) nor above 24:00.
 */
export function stepStayMinutes(base: number | null, delta: number): number | null {
  if (base === null) return delta > 0 ? Math.max(delta, MIN_STAY_MINUTES) : null
  let next: number
  if (Math.abs(delta) === 5) next = delta > 0 ? Math.floor(base / 5) * 5 + 5 : Math.ceil(base / 5) * 5 - 5
  else next = base + delta
  return Math.min(MAX_STAY_MINUTES, Math.max(MIN_STAY_MINUTES, next))
}
